import { FilterIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MobileFilterContent } from './MobileFilterContent';
import useHomePageStore from '@/store/useHomePageStore';
import { useTranslation } from 'react-i18next';
import { FilterModalProps } from './types';

export function FilterModal({
  isOpen,
  hasActiveFilters,
  onClose,
  onClearFilters
}: FilterModalProps) {
  const { t } = useTranslation('public', { keyPrefix: 'homepage.map.filterModal' });
  const {
    activityFilters,
    selectedAnimalTypes,
    selectedAdventureTypes,
    additionalFilters,
    setActivityFilters,
    setSelectedAnimalTypes,
    setSelectedAdventureTypes,
    setSelectedSeason,
    setAdditionalFilters,
  } = useHomePageStore();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-sm sm:max-w-lg max-h-[85vh] overflow-hidden p-0">
        <DialogHeader className="p-6 pb-4 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-emerald-50">
          <div className="flex items-center justify-between gap-4"> {/* Added gap for spacing */}
            <DialogTitle className="flex items-center gap-2 text-lg font-semibold flex-1 min-w-0">
              <FilterIcon className="h-5 w-5 text-emerald-600 shrink-0" />
              <span className="line-clamp-2 break-words">{t('title')}</span>
            </DialogTitle>
            {hasActiveFilters && (
              <Button
                onClick={onClearFilters}
                variant="outline"
                size="sm"
                className="h-8 px-3 text-xs border-emerald-500 text-emerald-600 hover:bg-emerald-50 shrink-0"
              >
                {t('clearAll')}
              </Button>
            )}
          </div>
        </DialogHeader>
        <div className="overflow-y-auto max-h-[60vh] p-6">
          <MobileFilterContent
            activityFilters={activityFilters}
            selectedAnimalTypes={selectedAnimalTypes}
            selectedAdventureTypes={selectedAdventureTypes}
            selectedSeason={additionalFilters.selectedSeason}
            groupSize={additionalFilters.groupSize}
            onActivityFilterChange={setActivityFilters}
            onAnimalTypeChange={setSelectedAnimalTypes}
            onAdventureTypeChange={setSelectedAdventureTypes}
            onSeasonChange={setSelectedSeason}
            onGroupSizeChange={(size: [number, number]) => setAdditionalFilters({...additionalFilters, groupSize: size})}
            onClearFilters={onClearFilters}
            showClearFilters={hasActiveFilters}
            hidePlaceboFilters={true}
          />
        </div>

        {/* Apply Button Footer - touch-friendly and safe area on notched devices */}
        <div className="p-4 pt-3 border-t border-gray-100 bg-gray-50 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <Button
            onClick={onClose}
            className="w-full min-h-[44px] h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 touch-manipulation"
          >
            {t('applyFilters')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
} 